import { Warehouse, ShoppingBag, Pill, Ship, Factory } from 'lucide-react';

const Industries = () => {
  const industries = [
    {
      icon: Warehouse,
      title: 'Warehousing & Logistics',
      description: 'Efficient pallet solutions for warehouse storage systems, distribution centers, and logistics operations. Our pallets are designed for compatibility with standard racking systems and material handling equipment.',
    },
    {
      icon: ShoppingBag,
      title: 'FMCG (Fast Moving Consumer Goods)',
      description: 'Reliable packaging for food, beverages, and consumer products requiring quick turnover and frequent handling. Our pallets ensure product safety and facilitate efficient distribution.',
    },
    {
      icon: Pill,
      title: 'Pharmaceutical',
      description: 'Clean, certified pallets meeting stringent pharmaceutical industry standards. ISPM-15 certified for international shipments of medicines and medical equipment.',
    },
    {
      icon: Ship,
      title: 'Export & Import',
      description: 'Export-ready packaging solutions with complete compliance documentation. We serve exporters shipping machinery, textiles, auto parts, and various industrial goods worldwide.',
    },
    {
      icon: Factory,
      title: 'Heavy Manufacturing',
      description: 'Heavy-duty pallets engineered for machinery, metal products, and industrial components. Custom load capacities up to 5000+ kg for demanding applications.',
    },
  ];

  return (
    <div className="font-inter" data-testid="industries-page">
      {/* Header */}
      <section className="bg-[#2B2B2B] text-white py-16" data-testid="industries-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-poppins font-bold text-4xl md:text-5xl mb-4">
            Industries We Serve
          </h1>
          <p className="text-xl text-gray-300">
            Trusted packaging solutions across diverse sectors
          </p>
          <div className="w-24 h-1 bg-[#B11217] mt-4"></div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-16 bg-white" data-testid="industries-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div
                  key={index}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                    }`}
                  data-testid={`industry-${index}`}
                >
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="w-20 h-20 bg-[#B11217] rounded-full flex items-center justify-center mb-6">
                      <Icon size={40} className="text-white" />
                    </div>
                    <h2 className="font-poppins font-bold text-3xl text-[#2B2B2B] mb-4">
                      {industry.title}
                    </h2>
                    <p className="text-gray-600 leading-relaxed text-lg">
                      {industry.description}
                    </p>
                  </div>
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <img
                      src={`/assets/images/${[
                        'industrial-packaging-banner.jpeg',
                        'industry-logistics.jpeg',
                        'wooden-pallets-banner.jpeg',
                        'industry-manufacturing.jpeg',
                        'warehouse-packaging.jpeg',
                      ][index]}`}
                      alt={industry.title}
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Industries */}
      <section className="py-16 bg-[#F2F2F2]" data-testid="additional-industries">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-poppins font-bold text-3xl text-[#2B2B2B] mb-12 text-center">
            We Also Serve
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[
              'Automotive',
              'Textiles',
              'Chemicals',
              'Electronics',
              'Agriculture',
              'Construction',
              'E-commerce',
              'Retail',
            ].map((industry) => (
              <div
                key={industry}
                className="bg-white p-6 text-center hover:shadow-lg transition-shadow border-b-4 border-[#B11217]"
                data-testid={`additional-industry-${industry.toLowerCase()}`}
              >
                <p className="font-poppins font-semibold text-[#2B2B2B]">{industry}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#B11217]" data-testid="industries-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="font-poppins font-bold text-3xl mb-4">
            Don't See Your Industry Listed?
          </h2>
          <p className="text-xl mb-8 text-gray-100">
            We provide custom solutions for businesses across all sectors. Contact us to discuss your requirements.
          </p>
          <a
            href="/contact"
            className="bg-white text-[#B11217] px-8 py-4 font-poppins font-semibold text-sm uppercase tracking-wide hover:bg-gray-100 transition-colors inline-block"
            data-testid="industries-cta-button"
          >
            Get in Touch
          </a>
        </div>
      </section>
    </div>
  );
};

export default Industries;